import { optimizeTractorPlan } from '../../../../supabase/functions/route-optimization/tractorPlan'
import { DispatchError, type DispatchRepository } from '../application/DispatchRepository'
import { editableLines } from '../domain/dispatch'
import type {
  ChangeItem,
  DispatchHistory,
  DispatchProposal,
  DispatchRoute,
  DispatchSettings,
  DispatchWorkspace,
  EditableLine,
  LineLoad,
  PlanDiff,
  PlanLoad,
  PublishedLine,
  RoadBudget,
  RouteLoad,
  StopKind,
  VersionContent,
} from '../domain/dispatch'

// Démonstration hors ligne (aucun Supabase configuré) et tests : quatre tracteurs, onze routes fictives. Les charges
// sont des estimations de démonstration (trajet fixe par route, transition 5 min dans un même secteur, 14 min sinon) :
// elles n'imitent pas le calcul du serveur et ne servent jamais de repli à la vraie répartition.

const ids = {
  plan: 'f0000000-0000-4000-a000-000000000001',
  sector: (n: number) => `f0100000-0000-4000-a000-00000000000${String(n)}`,
  route: (n: number) => `f0200000-0000-4000-a000-0000000000${String(n).padStart(2, '0')}`,
  equipment: (n: number) => `f0300000-0000-4000-a000-00000000000${String(n)}`,
  operator: (n: number) => `f0400000-0000-4000-a000-00000000000${String(n)}`,
  line: (n: number) => `f0500000-0000-4000-a000-00000000000${String(n)}`,
  version: (n: number) => `f0600000-0000-4000-a000-0000000000${String(n).padStart(2, '0')}`,
}

const sectors = [
  { id: ids.sector(1), name: 'Saint-Antoine', color: '#63a7ff', center: [45.765, -74.02] as const },
  { id: ids.sector(2), name: 'Bellefeuille', color: '#4cc38a', center: [45.81, -74.08] as const },
  { id: ids.sector(3), name: 'Saint-Jérôme', color: '#b98cff', center: [45.785, -74.0] as const },
  { id: ids.sector(4), name: 'Lafontaine', color: '#f2b24c', center: [45.805, -73.985] as const },
]

interface DemoRoute {
  n: number
  name: string
  sector: number
  category: 'RESIDENTIAL' | 'LARGE_SITE'
  residential: number
  commercial: number
  multiUnit: number
  travelMinutes: number
}

const demoRoutes: DemoRoute[] = [
  {
    n: 1,
    name: 'Saint-Antoine 1',
    sector: 1,
    category: 'RESIDENTIAL',
    residential: 33,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 19,
  },
  {
    n: 2,
    name: 'Saint-Antoine A',
    sector: 1,
    category: 'LARGE_SITE',
    residential: 0,
    commercial: 3,
    multiUnit: 2,
    travelMinutes: 12,
  },
  {
    n: 3,
    name: 'Bellefeuille 1',
    sector: 2,
    category: 'RESIDENTIAL',
    residential: 16,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 15,
  },
  {
    n: 4,
    name: 'Bellefeuille A',
    sector: 2,
    category: 'LARGE_SITE',
    residential: 0,
    commercial: 2,
    multiUnit: 1,
    travelMinutes: 7,
  },
  {
    n: 5,
    name: 'Saint-Antoine 2',
    sector: 1,
    category: 'RESIDENTIAL',
    residential: 15,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 19,
  },
  {
    n: 6,
    name: 'Saint-Jérôme 1',
    sector: 3,
    category: 'RESIDENTIAL',
    residential: 15,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 20,
  },
  {
    n: 7,
    name: 'Saint-Jérôme A',
    sector: 3,
    category: 'LARGE_SITE',
    residential: 0,
    commercial: 3,
    multiUnit: 1,
    travelMinutes: 10,
  },
  {
    n: 8,
    name: 'Saint-Antoine 3',
    sector: 1,
    category: 'RESIDENTIAL',
    residential: 14,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 16,
  },
  {
    n: 9,
    name: 'Lafontaine 1',
    sector: 4,
    category: 'RESIDENTIAL',
    residential: 17,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 18,
  },
  {
    n: 10,
    name: 'Lafontaine A',
    sector: 4,
    category: 'LARGE_SITE',
    residential: 0,
    commercial: 2,
    multiUnit: 1,
    travelMinutes: 9,
  },
  {
    n: 11,
    name: 'Saint-Antoine 4',
    sector: 1,
    category: 'RESIDENTIAL',
    residential: 15,
    commercial: 0,
    multiUnit: 0,
    travelMinutes: 17,
  },
]

const routeColors = ['#3B82F6', '#22C55E', '#A855F7', '#F59E0B']

function demoStops(route: DemoRoute): DispatchRoute['stops'] {
  const sector = sectors[route.sector - 1] ?? sectors[0]
  const [lat, lng] = sector?.center ?? [45.78, -74]
  const kinds: StopKind[] = [
    ...Array<StopKind>(route.residential).fill('RESIDENTIAL'),
    ...Array<StopKind>(route.commercial).fill('COMMERCIAL'),
    ...Array<StopKind>(route.multiUnit).fill('MULTI_UNIT'),
  ]
  const offset = (route.n % 4) * 0.004
  return kinds.map((kind, index) => ({
    id: `f0700000-0000-4000-a${String(route.n).padStart(3, '0')}-${String(index).padStart(12, '0')}`,
    kind,
    latitude: lat + offset + Math.floor(index / 6) * 0.0012,
    longitude: lng + offset + (index % 6) * 0.0016,
  }))
}

const routes: DispatchRoute[] = demoRoutes.map((route) => ({
  id: ids.route(route.n),
  name: route.name,
  color: routeColors[(route.sector - 1) % routeColors.length] ?? null,
  category: route.category,
  sectorId: ids.sector(route.sector),
  displayOrder: route.n,
  assignable: true,
  stops: demoStops(route),
}))

const equipments: DispatchWorkspace['equipments'] = [1, 2, 3, 4].map((n) => ({
  id: ids.equipment(n),
  label: `Tracteur ${String(n)}`,
  numero: `T-${String(n)}`,
  category: null,
  available: true,
}))

const operatorNames = ['Mathieu L.', 'Samuel B.', 'Gabriel R.', 'Kevin T.', 'Jonathan P.']
const operators: DispatchWorkspace['operators'] = operatorNames.map((name, index) => ({
  id: ids.operator(index + 1),
  name,
  eligible: true,
  assignedEquipmentId: index < 4 ? ids.equipment(index + 1) : null,
}))

const defaultSettings: DispatchSettings = {
  isDefault: true,
  updatedAt: null,
  updatedByName: null,
  residentialServiceSeconds: 300,
  commercialServiceSeconds: 600,
  multiUnitServiceSeconds: 900,
  balancedThresholdSeconds: 900,
  acceptableThresholdSeconds: 2100,
  referenceStartTime: '02:00',
  travelSpeedKmh: 25,
  travelDetourFactor: 1.4,
  roadDurationMultiple: 2,
  serviceDurations: [],
}

function withDurations(settings: DispatchSettings): DispatchSettings {
  const row = (category: StopKind, value: number) => ({
    category,
    defaultSeconds: value,
    observedSeconds: null,
    samples: 0,
    usedSeconds: value,
    source: 'DEFAULT',
  })
  return {
    ...settings,
    serviceDurations: [
      row('RESIDENTIAL', settings.residentialServiceSeconds),
      row('COMMERCIAL', settings.commercialServiceSeconds),
      row('MULTI_UNIT', settings.multiUnitServiceSeconds),
    ],
  }
}

const lock = (n: number) =>
  n === 10 ? 'POSITION_LOCKED' : [3, 4, 6, 7, 9].includes(n) ? 'ROUTE_LOCKED' : 'FREE'

function demoLines(order: number[][], operatorsByLine: number[]): EditableLine[] {
  return order.map((routeNumbers, index) => ({
    lineId: ids.line(index + 1),
    equipmentId: ids.equipment(index + 1),
    operatorEmployeeId: ids.operator(operatorsByLine[index] ?? index + 1),
    routes: routeNumbers.map((n) => ({ routeId: ids.route(n), lockMode: lock(n) })),
  }))
}

interface StoredVersion {
  id: string
  version: number
  note: string | null
  publishedAt: string
  lines: EditableLine[]
}

const sources = { travel: 'DEMO', service: 'DEFAULT', transition: 'DEMO' }

function routeLoad(route: DispatchRoute, settings: DispatchSettings): RouteLoad {
  const demo = demoRoutes.find((item) => ids.route(item.n) === route.id)
  const count = (kind: StopKind) => route.stops.filter((stop) => stop.kind === kind).length
  const serviceSeconds =
    count('RESIDENTIAL') * settings.residentialServiceSeconds +
    count('COMMERCIAL') * settings.commercialServiceSeconds +
    count('MULTI_UNIT') * settings.multiUnitServiceSeconds
  const travelSeconds = (demo?.travelMinutes ?? 0) * 60
  return {
    routeId: route.id,
    addressCount: route.stops.length,
    residentialCount: count('RESIDENTIAL'),
    commercialCount: count('COMMERCIAL'),
    multiUnitCount: count('MULTI_UNIT'),
    unlocatedCount: 0,
    travelSeconds,
    serviceSeconds,
    totalSeconds: travelSeconds + serviceSeconds,
  }
}

const sectorOfRoute = new Map(routes.map((route) => [route.id, route.sectorId]))
/** Transition de démonstration : 5 min dans un même secteur, 14 min sinon. */
function demoTransition(from: string, to: string): number {
  return sectorOfRoute.get(from) === sectorOfRoute.get(to) ? 300 : 840
}

function planLoad(lines: readonly EditableLine[], settings: DispatchSettings): PlanLoad {
  const loads = new Map(routes.map((route) => [route.id, routeLoad(route, settings)]))
  const lineLoads: LineLoad[] = lines.map((line) => {
    let travel = 0
    let service = 0
    let transition = 0
    const routeRows = line.routes.map((route, index) => {
      const load = loads.get(route.routeId)
      travel += load?.travelSeconds ?? 0
      service += load?.serviceSeconds ?? 0
      const previous = index > 0 ? line.routes[index - 1]?.routeId : undefined
      const before = previous ? demoTransition(previous, route.routeId) : 0
      transition += before
      return { routeId: route.routeId, transitionBeforeSeconds: before, transitionUnknown: false }
    })
    const total = travel + service + transition
    return {
      lineId: line.lineId,
      tractorId: line.equipmentId,
      operatorId: line.operatorEmployeeId,
      routeIds: line.routes.map((route) => route.routeId),
      routeCount: line.routes.length,
      travelSeconds: travel,
      transitionSeconds: transition,
      serviceSeconds: service,
      totalSeconds: total,
      estimatedFinishOffset: total,
      routes: routeRows,
      source: sources,
    }
  })
  const active = lineLoads.filter((line) => line.routeCount > 0)
  const totals = active.map((line) => line.totalSeconds)
  const last = active.reduce<LineLoad | null>(
    (best, line) => (!best || line.totalSeconds > best.totalSeconds ? line : best),
    null,
  )
  const spread = totals.length > 0 ? Math.max(...totals) - Math.min(...totals) : 0
  return {
    routes: [...loads.values()],
    lines: lineLoads,
    summary: {
      lastFinishSeconds: last?.totalSeconds ?? 0,
      lastLineId: last?.lineId ?? null,
      spreadSeconds: spread,
      activeLines: active.length,
      balance:
        spread <= settings.balancedThresholdSeconds
          ? 'BALANCED'
          : spread <= settings.acceptableThresholdSeconds
            ? 'ACCEPTABLE'
            : 'UNBALANCED',
      referenceStartTime: settings.referenceStartTime,
    },
    sources,
    settings,
  }
}

const routeName = (routeId: string) => routes.find((route) => route.id === routeId)?.name ?? null
const equipmentLabel = (equipmentId: string | undefined) =>
  equipments.find((equipment) => equipment.id === equipmentId)?.label ?? null
const operatorName = (operatorId: string | null | undefined) =>
  operators.find((operator) => operator.id === operatorId)?.name ?? null

function diff(before: readonly EditableLine[], after: readonly EditableLine[]): PlanDiff {
  const items: ChangeItem[] = []
  const lineOf = (lines: readonly EditableLine[], routeId: string) =>
    lines.find((line) => line.routes.some((route) => route.routeId === routeId))
  for (const line of after) {
    const previous = before.find((candidate) => candidate.lineId === line.lineId)
    if (!previous) {
      items.push({
        kind: 'LINE_ADDED',
        lineId: line.lineId,
        equipmentLabel: equipmentLabel(line.equipmentId),
      })
      continue
    }
    if (previous.operatorEmployeeId !== line.operatorEmployeeId)
      items.push({
        kind: 'OPERATOR_CHANGED',
        lineId: line.lineId,
        equipmentLabel: equipmentLabel(line.equipmentId),
        beforeLabel: operatorName(previous.operatorEmployeeId),
        afterLabel: operatorName(line.operatorEmployeeId),
      })
    const kept = line.routes.filter((route) => previous.routes.some((r) => r.routeId === route.routeId))
    const keptBefore = previous.routes.filter((route) => line.routes.some((r) => r.routeId === route.routeId))
    if (kept.map((r) => r.routeId).join() !== keptBefore.map((r) => r.routeId).join())
      items.push({
        kind: 'ROUTE_REORDERED',
        lineId: line.lineId,
        equipmentLabel: equipmentLabel(line.equipmentId),
        routeIds: line.routes.map((route) => route.routeId),
        routeNames: line.routes.map((route) => routeName(route.routeId)),
      })
  }
  for (const line of before)
    if (!after.some((candidate) => candidate.lineId === line.lineId))
      items.push({
        kind: 'LINE_REMOVED',
        lineId: line.lineId,
        equipmentLabel: equipmentLabel(line.equipmentId),
      })
  for (const route of routes) {
    const from = lineOf(before, route.id)
    const to = lineOf(after, route.id)
    if (from && to && from.lineId !== to.lineId)
      items.push({
        kind: 'ROUTE_MOVED',
        routeId: route.id,
        routeName: route.name,
        fromLabel: equipmentLabel(from.equipmentId),
        toLabel: equipmentLabel(to.equipmentId),
      })
    if (!from && to)
      items.push({
        kind: 'ROUTE_ADDED',
        routeId: route.id,
        routeName: route.name,
        equipmentLabel: equipmentLabel(to.equipmentId),
      })
    if (from && !to)
      items.push({
        kind: 'ROUTE_REMOVED',
        routeId: route.id,
        routeName: route.name,
        equipmentLabel: equipmentLabel(from.equipmentId),
      })
    const beforeLock = from?.routes.find((r) => r.routeId === route.id)?.lockMode
    const afterLock = to?.routes.find((r) => r.routeId === route.id)?.lockMode
    if (beforeLock && afterLock && beforeLock !== afterLock)
      items.push({
        kind: 'ROUTE_LOCK_CHANGED',
        routeId: route.id,
        routeName: route.name,
        before: beforeLock,
        after: afterLock,
      })
  }
  const count = (...kinds: string[]) => items.filter((item) => kinds.includes(item.kind)).length
  return {
    hasChanges: items.length > 0,
    items,
    counts: {
      total: items.length,
      routesAdded: count('ROUTE_ADDED'),
      routesRemoved: count('ROUTE_REMOVED'),
      routesMoved: count('ROUTE_MOVED'),
      linesReordered: count('ROUTE_REORDERED'),
      operatorsChanged: count('OPERATOR_CHANGED'),
      locksChanged: count('ROUTE_LOCK_CHANGED'),
      addressLocksChanged: 0,
    },
  }
}

function publishedLines(lines: readonly EditableLine[]): PublishedLine[] {
  return lines.map((line, index) => ({
    lineId: line.lineId ?? ids.line(index + 1),
    equipmentId: line.equipmentId,
    equipmentLabel: equipmentLabel(line.equipmentId),
    operatorEmployeeId: line.operatorEmployeeId,
    operatorName: operatorName(line.operatorEmployeeId),
    position: index + 1,
    routes: line.routes.map((route, rank) => {
      const info = routes.find((candidate) => candidate.id === route.routeId)
      return {
        routeId: route.routeId,
        rank: rank + 1,
        lockMode: route.lockMode,
        routeName: info?.name ?? null,
        routeColor: info?.color ?? null,
        routeCategory: info?.category ?? null,
      }
    }),
  }))
}

function clone(lines: readonly EditableLine[]): EditableLine[] {
  return lines.map((line) => ({ ...line, routes: line.routes.map((route) => ({ ...route })) }))
}

export interface FixtureDispatchOptions {
  /** Sans plan publié : premier brouillon de la saison. */
  empty?: boolean
  /** Opération ouverte : la publication attend sa fermeture. */
  operationOpen?: boolean
}

export function createFixtureDispatchRepository(options: FixtureDispatchOptions = {}): DispatchRepository {
  const published12 = demoLines(
    [
      [2, 1],
      [3, 4, 5],
      [6, 7, 8],
      [9, 10, 11],
    ],
    [1, 2, 3, 4],
  )
  let settings = withDurations(defaultSettings)
  let revision = options.empty ? 0 : 5
  let draft: EditableLine[] = options.empty
    ? []
    : demoLines(
        [
          [1, 2],
          [3, 4, 5],
          [6, 7, 8],
          [9, 10, 11],
        ],
        [1, 2, 3, 5],
      )
  const versions: StoredVersion[] = options.empty
    ? []
    : [
        {
          id: ids.version(10),
          version: 10,
          note: 'Essai 3 tracteurs',
          publishedAt: '2026-10-14T16:45:00-04:00',
          lines: clone(published12),
        },
        {
          id: ids.version(11),
          version: 11,
          note: 'Plan de début de saison',
          publishedAt: '2026-10-21T09:10:00-04:00',
          lines: clone(published12),
        },
        {
          id: ids.version(12),
          version: 12,
          note: 'Ajout de Lafontaine A',
          publishedAt: '2026-10-26T14:32:00-04:00',
          lines: clone(published12),
        },
      ]
  const operation: DispatchWorkspace['operation'] = options.operationOpen
    ? {
        id: 'f0800000-0000-4000-a000-000000000001',
        number: 7,
        status: 'OPEN',
        openedAt: '2026-10-26T01:40:00-04:00',
        ageHours: 3,
        stale: false,
      }
    : null

  // Lot E : plafond 0 comme en base tant que le propriétaire ne l'a pas fixé. Lot F : une proposition à la fois.
  let budget: RoadBudget = {
    budget: 'DISPATCH_ROAD_TIMES',
    month: new Date().toISOString().slice(0, 7),
    monthlyUnitCap: 0,
    usedUnits: 0,
    usedCalls: 0,
    remainingUnits: 0,
    updatedAt: null,
    updatedByName: null,
    canManage: true,
  }
  let proposal: DispatchProposal | null = null
  const transitionPairs = (scope: 'PLAN' | 'ALL_PAIRS') => {
    const draftRoutes = draft.flatMap((line) => line.routes.map((route) => route.routeId))
    return scope === 'PLAN'
      ? draft.reduce((sum, line) => sum + Math.max(0, line.routes.length - 1), 0)
      : draftRoutes.length * Math.max(0, draftRoutes.length - 1)
  }
  const proposalLoad = (lines: readonly EditableLine[]) => {
    const load = planLoad(lines, settings)
    return { summary: load.summary, lines: load.lines, sources: load.sources }
  }
  const sectorOverrides = new Map<string, string | null>()
  const latest = () => versions[versions.length - 1] ?? null
  const check = (expected: number) => {
    if (expected !== revision) throw new DispatchError('DISPATCH_DRAFT_CONFLICT')
  }
  const write = (lines: EditableLine[]) => {
    draft = lines.map((line) => ({ ...line, lineId: line.lineId ?? crypto.randomUUID() }))
    revision += 1
    return { planId: ids.plan, revision, changed: true }
  }

  function workspace(season: string): DispatchWorkspace {
    const version = latest()
    const base = version?.lines ?? []
    return {
      season,
      currentSeason: season,
      plan:
        revision === 0
          ? null
          : {
              id: ids.plan,
              revision,
              baseVersionId: version?.id ?? null,
              restoredFromVersion: null,
              updatedAt: null,
              updatedByName: 'Gabriel',
            },
      draft: {
        lines: draft.map((line, index) => ({
          lineId: line.lineId ?? ids.line(index + 1),
          equipmentId: line.equipmentId,
          operatorEmployeeId: line.operatorEmployeeId,
          position: index + 1,
          routes: line.routes.map((route, rank) => ({ ...route, rank: rank + 1 })),
        })),
        addressLocks: [],
      },
      published: version
        ? {
            versionId: version.id,
            version: version.version,
            note: version.note,
            publishedAt: version.publishedAt,
            publishedByName: 'Gabriel',
            lines: publishedLines(version.lines),
            addressLocks: [],
            effect: {
              status: 'APPLIED',
              appliedAt: version.publishedAt,
              errorCode: null,
              deferredOperationId: null,
            },
          }
        : null,
      changes: diff(base, draft),
      routes: routes.map((route) =>
        sectorOverrides.has(route.id) ? { ...route, sectorId: sectorOverrides.get(route.id) ?? null } : route,
      ),
      equipments,
      operators,
      suggestedLines: demoLines(
        [
          [2, 1],
          [3, 4, 5],
          [6, 7, 8],
          [9, 10, 11],
        ],
        [1, 2, 3, 4],
      ).map((line) => ({
        equipmentId: line.equipmentId,
        operatorEmployeeId: line.operatorEmployeeId,
        routes: line.routes.map((route) => ({ routeId: route.routeId, lockMode: 'FREE' as const })),
      })),
      sectors: sectors.map(({ id, name, color }) => ({ id, name, color })),
      settings,
      load: {
        draft: planLoad(draft, settings),
        published: version ? planLoad(version.lines, settings) : null,
      },
      operation,
    }
  }

  return {
    getWorkspace: (season) => Promise.resolve(workspace(season)),
    saveDraft(command) {
      check(command.expectedRevision)
      return Promise.resolve(write(clone(command.lines)))
    },
    resetDraft(command) {
      check(command.expectedRevision)
      return Promise.resolve(write(clone(latest()?.lines ?? [])))
    },
    restoreVersion(command) {
      check(command.expectedRevision)
      const version = versions.find((candidate) => candidate.id === command.versionId)
      if (!version) return Promise.reject(new DispatchError('DISPATCH_VERSION_NOT_FOUND'))
      return Promise.resolve(write(clone(version.lines)))
    },
    getPublicationPreview(season) {
      const current = workspace(season)
      const version = latest()
      return Promise.resolve({
        revision,
        baseVersionId: version?.id ?? null,
        baseVersion: version?.version ?? null,
        nextVersion: (version?.version ?? 0) + 1,
        changes: current.changes,
        validation: {
          blocking: draft.some((line) => line.routes.length > 0 && line.operatorEmployeeId === null)
            ? [{ code: 'LINE_WITHOUT_OPERATOR', equipmentLabel: 'Un tracteur' }]
            : [],
          warnings: [],
        },
        sectorGaps: [
          {
            routeId: ids.route(7),
            routeName: 'Saint-Jérôme A',
            declaredSectorId: ids.sector(3),
            locations: 4,
            outside: 2,
            derived: [
              { sectorId: ids.sector(3), locations: 2 },
              { sectorId: ids.sector(1), locations: 2 },
            ],
          },
        ],
        operation,
      })
    },
    publish(command) {
      check(command.expectedRevision)
      if (command.baseVersionId !== (latest()?.id ?? null))
        return Promise.reject(new DispatchError('DISPATCH_PUBLICATION_CONFLICT'))
      const changes = diff(latest()?.lines ?? [], draft)
      if (!changes.hasChanges) return Promise.reject(new DispatchError('DISPATCH_NOTHING_TO_PUBLISH'))
      const version = (latest()?.version ?? 0) + 1
      const id = ids.version(version)
      versions.push({
        id,
        version,
        note: command.note || null,
        publishedAt: new Date().toISOString(),
        lines: clone(draft),
      })
      revision += 1
      return Promise.resolve({
        versionId: id,
        version,
        revision,
        effect: operation ? 'PENDING' : 'APPLIED',
        operation,
        changes: changes.counts,
      })
    },
    applyPending() {
      return operation
        ? Promise.reject(new DispatchError('DISPATCH_OPERATION_OPEN'))
        : Promise.reject(new DispatchError('DISPATCH_NOTHING_PENDING'))
    },
    getHistory(): Promise<DispatchHistory> {
      return Promise.resolve({
        versions: [...versions].reverse().map((version, index, list) => ({
          id: version.id,
          version: version.version,
          note: version.note,
          publishedAt: version.publishedAt,
          publishedByName: 'Gabriel',
          restoredFromVersion: null,
          lastFinishSeconds: planLoad(version.lines, settings).summary.lastFinishSeconds,
          referenceStartTime: settings.referenceStartTime,
          changes: diff(list[index + 1]?.lines ?? [], version.lines).counts,
          effect: {
            status: 'APPLIED',
            appliedAt: version.publishedAt,
            errorCode: null,
            deferredOperationId: null,
          },
        })),
        events: [...versions].reverse().map((version, index) => ({
          id: 100 - index,
          kind: 'PUBLISHED',
          details: { version: version.version },
          version: version.version,
          revision: null,
          actorName: 'Gabriel',
          occurredAt: version.publishedAt,
        })),
      })
    },
    getVersion(versionId): Promise<VersionContent> {
      const version = versions.find((candidate) => candidate.id === versionId)
      if (!version) return Promise.reject(new DispatchError('DISPATCH_VERSION_NOT_FOUND'))
      const load = planLoad(version.lines, settings)
      return Promise.resolve({
        id: version.id,
        season: '2026-2027',
        version: version.version,
        note: version.note,
        publishedAt: version.publishedAt,
        publishedByName: 'Gabriel',
        lines: publishedLines(version.lines),
        addressLocks: [],
        load,
        loadAtPublication: load,
      })
    },
    updateSettings(command) {
      if (command.balancedThresholdSeconds >= command.acceptableThresholdSeconds)
        return Promise.reject(new DispatchError('DISPATCH_SETTINGS_INVALID'))
      settings = withDurations({ ...settings, ...command, isDefault: false, updatedByName: 'Démo' })
      return Promise.resolve(settings)
    },
    setRouteSector(routeId, sectorId) {
      sectorOverrides.set(routeId, sectorId)
      return Promise.resolve()
    },
    getRoadBudget: () => Promise.resolve(budget),
    setRoadBudget(monthlyUnitCap) {
      budget = {
        ...budget,
        monthlyUnitCap,
        remainingUnits: Math.max(0, monthlyUnitCap - budget.usedUnits),
        updatedByName: 'Démo',
      }
      return Promise.resolve(budget)
    },
    estimateRoadTimes: (_season, scope) => {
      const units = transitionPairs(scope)
      return Promise.resolve({
        units,
        calls: Math.min(units, draft.flatMap((line) => line.routes).length),
        budget,
      })
    },
    computeRoadTimes() {
      const units = transitionPairs('PLAN')
      if (budget.usedUnits + units > budget.monthlyUnitCap)
        return Promise.reject(new DispatchError('PAID_API_BUDGET_EXCEEDED'))
      budget = {
        ...budget,
        usedUnits: budget.usedUnits + units,
        usedCalls: budget.usedCalls + units,
        remainingUnits: budget.monthlyUnitCap - budget.usedUnits - units,
      }
      return Promise.resolve({
        status: 'DONE',
        units,
        calls: units,
        stored: units,
        missing: 0,
        replayed: false,
        budget,
      })
    },
    optimize(command) {
      if (command.useRoadTimes) return Promise.reject(new DispatchError('PAID_API_BUDGET_EXCEEDED'))
      const loads = new Map(routes.map((route) => [route.id, routeLoad(route, settings)]))
      const routeIds = draft.flatMap((line) => line.routes.map((route) => route.routeId))
      if (routeIds.length < 2)
        return Promise.reject(new DispatchError('DISPATCH_OPTIMIZATION_TOO_FEW_ROUTES'))
      const result = optimizeTractorPlan({
        lines: draft.map((line, index) => ({
          lineId: line.lineId ?? ids.line(index + 1),
          routes: line.routes,
        })),
        seconds: Object.fromEntries(routeIds.map((id) => [id, loads.get(id)?.totalSeconds ?? 0])),
        transitions: Object.fromEntries(
          routeIds.map((from) => [
            from,
            Object.fromEntries(routeIds.map((to) => [to, demoTransition(from, to)])),
          ]),
        ),
      })
      const proposed: EditableLine[] = draft.map((line, index) => ({
        ...line,
        routes: (result.lines[index]?.routeIds ?? []).map((routeId) => ({
          routeId,
          lockMode:
            draft.flatMap((item) => item.routes).find((route) => route.routeId === routeId)?.lockMode ??
            'FREE',
        })),
      }))
      const counts = diff(draft, proposed).counts
      const before = proposalLoad(draft)
      const after = proposalLoad(proposed)
      proposal = {
        runId: 'f0900000-0000-4000-a000-000000000001',
        status: 'REVIEW',
        season: command.season,
        planId: ids.plan,
        baseRevision: revision,
        stale: false,
        lines: proposed.map((line, index) => ({
          lineId: line.lineId ?? ids.line(index + 1),
          equipmentId: line.equipmentId,
          operatorEmployeeId: line.operatorEmployeeId,
          position: index + 1,
          routes: line.routes.map((route, rank) => ({ ...route, rank: rank + 1 })),
        })),
        before,
        after,
        changes: {
          routesMoved: counts.routesMoved,
          linesReordered: counts.linesReordered,
          gainSeconds: before.summary.lastFinishSeconds - after.summary.lastFinishSeconds,
        },
        parameters: { useRoadTimes: false },
        usage: { provider: 'NONE', units: 0 },
        createdAt: new Date().toISOString(),
        createdByName: 'Démo',
      }
      return Promise.resolve(proposal)
    },
    getProposal: () =>
      Promise.resolve(
        proposal?.status === 'REVIEW' ? { ...proposal, stale: proposal.baseRevision !== revision } : null,
      ),
    applyProposal() {
      if (proposal?.status !== 'REVIEW')
        return Promise.reject(new DispatchError('DISPATCH_OPTIMIZATION_CLOSED'))
      if (proposal.baseRevision !== revision)
        return Promise.reject(new DispatchError('DISPATCH_PROPOSAL_STALE'))
      write(editableLines(proposal.lines))
      proposal = { ...proposal, status: 'APPLIED' }
      return Promise.resolve(proposal)
    },
    dismissProposal() {
      if (proposal?.status !== 'REVIEW')
        return Promise.reject(new DispatchError('DISPATCH_OPTIMIZATION_CLOSED'))
      proposal = { ...proposal, status: 'CANCELLED' }
      return Promise.resolve(proposal)
    },
  }
}
